/* PIPE-INST T5 (firmas #122, #123, #124, #126) — LOS COPYS DE LA CURACIÓN.
 *
 * MÓDULO HERMANO DE `lib/temasCopys.js`, y por las mismas dos razones que aquél
 * existe: hay una prueba que fija estos textos VERBATIM (letra por letra), y una
 * prueba que leyera del JSX tendría que montar la pantalla entera para comprobar
 * una coma. Aquí el copy es un dato: la pantalla lo pinta y la prueba lo compara.
 *
 * POR QUÉ UN ARCHIVO NUEVO Y NO MÁS LÍNEAS EN `temasCopys.js`: son dos
 * superficies distintas (la lista de temas y la cola de una sola cola de
 * revisión) y el archivo viejo ya iba por veinte copys. Cuando el segundo idioma
 * exista, los dos se mudan juntos al árbol de i18n de una vez.
 *
 * SUPERFICIE DE UN SOLO IDIOMA: ESPAÑOL. Cero `t()`, cero llaves de i18n. Es la
 * misma decisión firmada de #118, con el mismo porqué: una llave con una sola
 * traducción es una promesa de segundo idioma que nadie ha hecho.
 *
 * ── LA GRAMÁTICA ES CÓDIGO, NO PLANTILLA ────────────────────────────────────
 *
 * Todo copy con una cifra es una FUNCIÓN con su singular explícito. «1 preguntas
 * para revisar» es la clase de descuido que le dice al profesor que nadie leyó
 * esta pantalla, y el singular es una regla del producto que se prueba sola.
 */

/* ════════════════════════════════════════════════════════════════════════════
 *  BANNERS MOMENTÁNEOS (B1–B7)
 *
 *  Estilo de la casa —el mismo toast del copy 10, «Empezamos a generar…»—, ~6 s,
 *  y EN FILA cuando salen varios: nunca encimados. Sustituyen al letrero
 *  persistente de paquete que murió en #122, que describía una foto vieja y la
 *  seguía cantando al día siguiente.
 * ════════════════════════════════════════════════════════════════════════════ */

/** Milisegundos que vive un banner. Seis segundos es lo que tarda en leerse un
 *  renglón sin prisa y lo bastante poco para que tres seguidos no tapen la
 *  pantalla mientras el profesor trabaja. */
export const BANNER_MS = 6000;

/* B1 — el tema terminó COMPLETO. */
export function bannerTemaListo(t, tema, n) {
  return t('groups.bank.curacion.banner_topic_ready', { topic: tema, count: n });
}

/* B2 — el tema terminó, pero sin cubrir su inventario. */
export function bannerTemaAMedias(t, tema, n) {
  return t('groups.bank.curacion.banner_topic_half', { topic: tema, count: n });
}

/* B3 — el tema no dejó ni una pregunta.
 *
 * SU SEGUNDA FRASE ES #T23 VERBATIM («Márcalo y vuelve a intentar.» /
 * «Select it and try again.»), que es la nota que la pantalla de temas pinta
 * bajo el tema fallido. Las dos tienen que decir lo mismo EN LOS DOS IDIOMAS, y
 * el candado B lo comprueba con un `endsWith` en vez de confiarlo. */
export function bannerTemaFallido(t, tema) {
  return t('groups.bank.curacion.banner_topic_failed', { topic: tema });
}

/* B4 — la cola de un tema llegó a cero. Su llave es
 * `groups.bank.curacion.banner_topic_reviewed`. */

/* B5 — la publicación se creó. */
export function bannerPublicacionCreada(t, k) {
  return t('groups.bank.publicar.created_banner', { k });
}

/* B6 / B7 — el desenlace de una regeneración. SIEMPRE en banner y jamás como un
 * aviso estático dentro de la card (firma #124). Sus llaves son
 * `groups.bank.curacion.{banner_regenerated, banner_regen_failed}`. */

/* El copy de «hoy ya no hay cupo» NO SE DUPLICA AQUÍ. Lo emite el servidor
 * (`COPY_SIN_CUPO_HOY`, firma de T4) y la pantalla lo pinta tal cual en su
 * banner. Copiarlo sería estrenar una segunda verdad sobre la misma frase. */

/* ════════════════════════════════════════════════════════════════════════════
 *  LA PANTALLA DE CURACIÓN (C4–C36)
 * ════════════════════════════════════════════════════════════════════════════ */

/* AQUÍ VIVÍA `CURACION` — SUS 22 COPYS SE FUERON AL ÁRBOL EN LA TANDA 26.
 *
 * Todos eran constantes planas, sin una cifra que decidir, así que la pantalla
 * los lee ahora directamente por `t()` bajo `dashboard:groups.bank.curacion.*`.
 * El mapeo, para quien venga a buscarlos:
 *
 *   revisarPreguntas  -> review_btn        (lo pinta la pantalla de TEMAS)
 *   pestanas.*        -> tab_pending | tab_approved | tab_discarded
 *   vacio*            -> empty_pending | empty_approved | empty_discarded
 *   respuestaCorrecta -> correct_answer    porQueLaCorrecta -> why_correct
 *   confusion         -> misconception     loQueVera        -> why_wrong
 *   verExplicacion    -> view_explanation
 *   aprobar/editar/descartar/regenerar -> approve|edit|discard|regenerate
 *   guardar           -> save              cambiosGuardados -> saved_toast
 *   preguntaDescartada-> discarded_toast   restaurar        -> restore
 *   regenerando       -> regenerating      aprobarTodas     -> approve_all
 *
 * `cancelar` NO ESTRENÓ LLAVE, y es la única decisión de este mapeo que no es
 * mecánica: los cinco «Cancelar» del carril (modo edición, modal de regenerar,
 * modal de aprobar todas, modal de publicar y modal de borrar grupo) pintan
 * `common:cancel`, que ya existía y ya decía exactamente eso en los dos idiomas.
 * Cinco llaves para un mismo texto son una invitación a que un día digan cosas
 * distintas sin que nadie lo note.
 *
 * LOS PORQUÉS DE PRODUCTO NO SE PIERDEN — que «Por qué la correcta es correcta»
 * sustituyó a «Explicación» a secas (#129), que la confusión es SÓLO LECTURA
 * desde #137, que «Explicación de por qué esta opción es incorrecta» nombra el
 * campo y no el canal (#138) — porque son decisiones sobre la pantalla y siguen
 * escritas donde la pantalla las aplica.
 */


/* C10 — la etiqueta de nivel. LA DERIVA EL SISTEMA desde el nivel de Bloom, con
 * las MISMAS fronteras que `validacion.dificultad_de_bloom` del servidor
 * (1-2 fácil, 3-4 medio, 5-6 difícil). Se calcula aquí y no se lee `difficulty`
 * para que la etiqueta no pueda desviarse del Bloom que la card enseña. */
export function etiquetaDeNivel(t, bloom) {
  const banda = bandaDeNivel(bloom);
  return banda ? t('groups.bank.curacion.level_' + banda) : '';
}

/* LA BANDA DE BLOOM COMO CÓDIGO — TANDA 26 (firma #234), Y ES UN ARREGLO REAL.
 *
 * Hasta hoy el color del chip salía de `NIVEL_COLOR[etiquetaDeNivel(bloom)]`, o
 * sea: SE INDEXABA POR TEXTO DE USUARIO. Traducir la etiqueta a EASY/MEDIUM/HARD
 * dejaba `NIVEL_COLOR['EASY']` en `undefined`, la expresión caía al `|| ''` y el
 * chip se pintaba GRIS — sin excepción, sin hueco en la pantalla y sin una sola
 * prueba en rojo. Es la trampa concreta de esta migración, y por eso el arreglo
 * es estructural y no un parche: la etiqueta y el color salen del mismo Bloom
 * por dos caminos que no se miran entre sí, así que un idioma nuevo no puede
 * volver a apagar el color.
 *
 * Las fronteras son las MISMAS que `validacion.dificultad_de_bloom` del servidor
 * (1-2 fácil, 3-4 medio, 5-6 difícil), y se derivan del Bloom y no de
 * `difficulty` para que la etiqueta no pueda desviarse del que la card enseña.
 * Fuera de 1–6 devuelve cadena vacía: sin banda no hay chip, que es lo que hacía
 * antes y sigue siendo lo correcto. */
export function bandaDeNivel(bloom) {
  const n = Number(bloom);
  if (n >= 1 && n <= 2) return 'easy';
  if (n >= 3 && n <= 4) return 'medium';
  if (n >= 5 && n <= 6) return 'hard';
  return '';
}

/* C5 — el subtítulo con los tres contadores. Cada cifra con su singular.
 *
 * TRES LLAVES Y NO UNA. «por revisar» NO cambia con la cifra en ninguno de los
 * dos idiomas, así que no declara formas de plural; los otros dos sí las
 * declaran —aunque en inglés «approved» y «discarded» digan lo mismo en singular
 * y en plural— porque el ESPAÑOL sí las necesita y la llave tiene que existir en
 * los dos archivos con la misma forma. El separador (la coma, t7-3 remate R2) es código, no copy. */
export function subtituloDeCuracion(t, p, a, d) {
  return [
    t('groups.bank.curacion.subtitle_pending', { count: p }),
    t('groups.bank.curacion.subtitle_approved', { count: a }),
    t('groups.bank.curacion.subtitle_discarded', { count: d }),
  ].join(', ');
}

/* C13 (T5.1, firma #130) — LAS CINCO VALIDACIONES DE VACÍO, UNA POR CASO.
 *
 * ── POR QUÉ CINCO Y NO UNA ──────────────────────────────────────────────────
 *
 * Porque un «faltan campos» genérico obliga al profesor a mirar las siete cajas
 * de la card para encontrar cuál. Cada copy nombra EL campo, y el que se dispara
 * es el del PRIMER vacío que se encuentra al recorrerlas en el orden en que
 * están en pantalla: enunciado, las cuatro opciones, la explicación y las seis
 * líneas de confusión.
 *
 * ── Y POR QUÉ LA CORRECTA TIENE EL SUYO PROPIO ──────────────────────────────
 *
 * Es la única opción que el profesor NO puede mover de sitio (ver
 * `curacion.editar`), así que decirle «ninguna opción puede quedar vacía» sobre
 * la correcta lo mandaría a revisar las cuatro. Nombrarla ahorra ese viaje.
 */
export const C13 = {
  enunciado: 'groups.bank.curacion.empty_stem',
  correcta: 'groups.bank.curacion.empty_correct',
  opcion: 'groups.bank.curacion.empty_option',
  explicacion: 'groups.bank.curacion.empty_explanation',
  // T5.3 (#138) — AQUÍ VIVÍA `confusion: 'Las confusiones no pueden quedar
  // vacías.'`, Y SE RETIRA CON SU CAJA.
  //
  // Hablaba de DOS campos por bloque, y desde #137 sólo uno se edita: la
  // confusión pasó a sólo lectura. Un banner que nombra un campo que el profesor
  // no puede tocar lo manda a buscar una caja que no existe — que es exactamente
  // el defecto que la familia C13 nació para cerrar, por el otro extremo.
  //
  // El copy nuevo nombra el campo que SÍ está en pantalla, con las mismas
  // palabras que su etiqueta (`why_wrong`).
  racional: 'groups.bank.curacion.empty_rationale',
};

/* TANDA 26 (firma #234): `C13` PASÓ DE GUARDAR CINCO TEXTOS A GUARDAR CINCO
 * LLAVES, y la estructura NO se disuelve por una razón concreta —
 * `C13_DEL_SERVIDOR` (abajo) mapea SEIS códigos del servidor sobre estos cinco,
 * y ese mapa es la pieza que cerró el peor defecto del smoke. Si `C13` se
 * deshiciera en llamadas sueltas a `t()`, el mapa tendría que repetir las llaves
 * a mano y las dos mitades podrían separarse sin que nada se pusiera rojo.
 * Guardando llaves, la pantalla hace `t(C13.x)` y el mapa sigue diciendo
 * exactamente lo que decía. */

/* LO QUE EL SERVIDOR CONTESTA, TRADUCIDO A LOS MISMOS CINCO COPYS.
 *
 * EL DEFECTO QUE ESTE MAPA CIERRA, y era el peor de los cuatro del smoke: la
 * validación dura del servidor ya existía y ya devolvía un 400 con su código,
 * pero el cliente lo pintaba con `COPYS.documentoError` — «No se pudo procesar
 * este documento. Sube el archivo de nuevo o intenta con otro.». O sea: el
 * profesor borraba la explicación, tocaba Guardar, y la pantalla le decía que
 * volviera a SUBIR EL ARCHIVO. Un mensaje falso es peor que ninguno.
 *
 * Los dos códigos de FORMA (`opciones_invalidas`, `racionales_invalidos`) caen
 * en el copy de su familia: para el profesor, una lista de opciones que no llega
 * a cuatro es el mismo problema que una opción vacía.
 */
export const C13_DEL_SERVIDOR = {
  enunciado_vacio: C13.enunciado,
  opcion_vacia: C13.opcion,
  opciones_invalidas: C13.opcion,
  explicacion_vacia: C13.explicacion,
  racional_vacio: C13.racional,
  racionales_invalidos: C13.racional,
};

/* C16 — el modal de regenerar. Sus dos textos propios están en
 * `groups.bank.curacion.{modal_regen_title, modal_regen_body}`; su botón de
 * confirmar reusa `regenerate` (es el MISMO texto que el botón de la card, y
 * darle llave propia dejaría abierta la puerta a que un día discreparan), y su
 * «Cancelar» pinta `common:cancel`. */

/* C35 — el modal de «Aprobar todas», con su singular. */
export function tituloAprobarTodas(t, p) {
  // EL SINGULAR NO LLEVA NÚMERO en ninguno de los dos idiomas —«¿Aprobar la
  // pregunta pendiente?»— y por eso su forma `_one` no interpola `{{count}}`.
  return t('groups.bank.curacion.modal_approve_all_title', { count: p });
}
/* T5.1 (firma #131) — «AÚN» AL PRINCIPIO, y no es un adorno: el modal aparece
 * justo cuando el profesor está a punto de aprobar en bloque algo que no ha
 * leído, y lo que necesita saber en esa mitad de segundo es que la puerta SIGUE
 * ABIERTA. «Podrás» describe una posibilidad futura; «Aún podrás» dice que no ha
 * cerrado nada. */
/* T5.3 (firma #139) — LA SEGUNDA FRASE, Y NO ES ADORNO.
 *
 * «Aprobar TODAS» sobre una cola que ya tiene descartadas se lee como «va a
 * resucitar lo que tiré». Es la duda exacta que frena el toque, y el modal
 * existe para quitarla — así que la contesta en el mismo renglón en que la
 * provoca. (El producto nunca resucita una descartada: es la garantía que
 * heredamos de `test_g2` y que el camino de publicar también cumple.) */
/* Su cuerpo es `groups.bank.curacion.modal_approve_all_body`. El botón de
 * confirmar reusa `approve_all` —el mismo texto que el botón sobre la cola— y
 * «Cancelar» pinta `common:cancel`.
 *
 * NOTA SOBRE `MODAL_APROBAR_TODAS.cancelar`: existía y NO SE PINTABA NUNCA. El
 * JSX usaba `MODAL_REGENERAR.cancelar` en los DOS modales, así que era una
 * constante muerta con el mismo texto que su gemela viva. Con `common:cancel` el
 * problema desaparece por construcción: no hay dos, hay una. */

/* AQUÍ VIVÍAN `MODAL_SIN_REVISAR` Y `cuerpoSinRevisar` (C24) — RETIRADOS EN T5.2.
 *
 * Lo que decían —«se aprueban tal cual, sin que las hayas visto»— no se pierde:
 * lo dice ahora el modal de publicar, en una sola línea condicional y sólo
 * cuando de verdad hay algo sin revisar entre lo marcado (`LINEA_SIN_REVISAR`,
 * abajo). La diferencia es que allí el profesor está mirando la lista completa
 * de lo que va a mandar, y aquí sólo veía un tema. */

/* ════════════════════════════════════════════════════════════════════════════
 *  LA PUBLICACIÓN (C28–C33)
 * ════════════════════════════════════════════════════════════════════════════ */

/* AQUÍ VIVÍA `PUBLICACION` — SUS SIETE COPYS SE FUERON AL ÁRBOL EN LA TANDA 26.
 *
 * Eran constantes planas, sin una cifra ni un singular que decidir, así que no
 * dejaban gramática detrás: la pantalla las lee ahora directamente por `t()`.
 *
 *   · `publicar`, `sinNadaQuePublicar`, `tituloModal`, `yaPublicado`
 *     → `dashboard:groups.bank.publicar.{btn, nothing_to_publish, modal_title,
 *        already_published}`
 *   · `confirmar` («Publicar») NO estrena llave: es el MISMO texto que el botón,
 *     y darle una propia dejaría abierta la puerta a que un día dijeran cosas
 *     distintas. Reusa `publicar.btn`.
 *   · `cancelar` tampoco: todo «Cancelar» del carril pinta `common:cancel`, que
 *     ya existía y ya decía exactamente eso en los dos idiomas.
 *   · `tagPublicado` y `tagParcialmentePublicado` se fueron a
 *     `groups.bank.doc.{tag_published, tag_partly_published}`, porque son de la
 *     card del BANCO y no del modal de publicar.
 *
 * Los porqués de producto que vivían en estos comentarios NO se pierden — el tag
 * sin número, el botón apagado y explicado, el «Publicado» que significa TODOS
 * desde #140 — porque son decisiones sobre la pantalla, y siguen escritas donde
 * la pantalla las aplica.
 */


/* F6 (T5.2) — LA LÍNEA CONDICIONAL DEL MODAL.
 *
 * Sale SÓLO si algo de lo marcado trae pendientes, y es lo que hace honesta a la
 * publicación sin revisar: el profesor tiene que enterarse de que va a mandar
 * material que nadie leyó, en el momento exacto en que lo va a mandar. Cuando
 * todo está revisado no se pinta — un aviso que sale siempre deja de leerse. */
// La llave es `groups.bank.publicar.unreviewed_line`; la pantalla la lee con
// `t()` y sigue pintándola SÓLO cuando de verdad hay algo sin revisar.

/* F4 (T5.2) — EL RENGLÓN DE CADA TEMA DENTRO DEL MODAL.
 *
 * `n` son las preguntas VIVAS del tema (las que van a entrar a la card); `m`,
 * cuántas de ésas están sin revisar. La segunda mitad sólo aparece cuando hay
 * algo sin revisar: en un tema curado entero, un «· 0 sin revisar» sería ruido
 * que el profesor tiene que leer para descubrir que no dice nada.
 *
 * Los dos singulares son explícitos, como manda la gramática de la casa. */
export function renglonDeTemaPublicable(t, tema, n, m = 0) {
  const cuantas = t('groups.bank.publicar.topic_row', { topic: tema, count: n });
  if (!m) return cuantas;
  // La `, ` es un SEPARADOR, no copy (t7-3 remate R2): se compone aquí y no viaja en la llave,
  // igual que en `renglonDeTema`. Lo que sí lleva llave —con su singular— es el
  // trozo que cuenta las que faltan por revisar.
  return `${cuantas}, ${t('groups.bank.publicar.topic_row_unreviewed', { count: m })}`;
}

/* C30 — el cuerpo del modal. Anuncia la card que va a nacer, con SU número. */
export function cuerpoDePublicacion(t, k, documento, total) {
  // UNA SOLA LLAVE con `_one`/`_other`, y no un trozo variable pegado a tres
  // fijos: el español partía la frase en «con N preguntas en total» + el resto,
  // y eso obliga a que las dos mitades encajen en cualquier idioma. En inglés no
  // encajan —«with 1 question in total» va en otro sitio de la oración— así que
  // la frase entera viaja completa en cada forma del plural.
  return t('groups.bank.publicar.modal_body', { k, document: documento, count: total });
}

/* C33 — los dos chips de tema que estrena T5, los dos FIJOS desde T5.2.
 *
 * ── POR QUÉ «Publicado» PERDIÓ SU NÚMERO (T5.2) ─────────────────────────────
 *
 * Hasta T5.1 era `Publicado #{k}`, con el razonamiento de que el número le
 * permitía al profesor casar el tema con la card concreta que ven sus alumnos.
 * Muere por la MISMA razón por la que murió el número de la etiqueta del modal:
 * **el `#{k}` no significa nada mirando un tema.** Un profesor que ve «Publicado
 * #2» junto a «Ley de Hess» no tiene forma de saber qué es la card 2 sin ir a
 * buscarla, y con el REMIX de #133 la cosa empeora — un tema puede estar en
 * varias cards, así que el número de su chip sería sólo el de la PRIMERA: una
 * media verdad presentada como un dato.
 *
 * La numeración sigue viva donde sí se entiende sola: en el nombre de la card
 * del alumno («#1 Mercado de deuda») y en el banner que la anuncia al nacer. */
/* Los dos chips se leen ahora por `t()` desde `textoDelChip` (en `temasCopys`),
 * bajo `groups.bank.temas.{chip_reviewed, chip_published}`. La razón de que
 * vivan bajo `temas` y no bajo `curacion` es simple: los pinta la pantalla de
 * TEMAS, y el árbol de llaves sigue a la pantalla, no al archivo donde el copy
 * nació. */

/* ════════════════════════════════════════════════════════════════════════════
 *  BORRAR EL GRUPO (C37 + el modal #113-v2)
 * ════════════════════════════════════════════════════════════════════════════ */

/* C37 — la entrada. Su llave es `dashboard:groups.delete_group_btn`. */

/* EL MODAL, VERBATIM DE LA FIRMA #128 — NI UNA LETRA DISTINTA.
 *
 * El cuerpo enumera lo que muere en el orden en que al profesor le importa, y no
 * usa un solo eufemismo: «para siempre», «no se puede deshacer», y la lista
 * completa. Es la única acción del producto de profesor que destruye material
 * pagado, así que el aviso no puede ser suave.
 *
 * ── LA ENMIENDA DE T5.1: FUERA «flashcards» ─────────────────────────────────
 *
 * #113-v2 escribió «…con sus preguntas y flashcards». **El carril institucional
 * no genera flashcards**: la mitad de Call B es del pipeline del consumidor y un
 * documento del banco jamás produce una. Prometer que se van a borrar unas
 * flashcards que nunca existieron le enseña al profesor una función que el
 * producto no tiene — y en el aviso más serio de toda su superficie. Se retira
 * la palabra y el resto del copy no se mueve un carácter.
 *
 * Y NO PIDE ESCRIBIR EL NOMBRE DEL GRUPO: la firma no lo pide, y añadir una
 * fricción que nadie firmó sería inventar producto. Lo que sí hace el botón es
 * decir exactamente lo que va a pasar, en vez de un «Eliminar» a secas. */
/* SUS CUATRO TEXTOS SE FUERON A `dashboard:groups.delete_group_*` EN LA TANDA 26,
 * y van bajo `groups.*` y no bajo `groups.bank.*` a propósito: lo que se destruye
 * es el GRUPO entero —con su código, su lista de alumnos y su banco— y el árbol
 * de llaves sigue a lo que la acción destruye.
 *
 * El «Cancelar» del modal, que es el botón PRIMARIO (naranja) a propósito, pinta
 * `common:cancel` como los otros cuatro del carril.
 *
 * LA ENMIENDA DE T5.1 VIAJA CON EL TEXTO Y AHORA TIENE CANDADO: el cuerpo NO
 * dice «flashcards» en ninguno de los dos idiomas, y `profeBilingue.tanda26.test.js`
 * lo comprueba. Al traducir, la tentación de «completar» la lista es real — y
 * prometer que se borran unas flashcards que el carril institucional nunca genera
 * es enseñarle al profesor una función que el producto no tiene, en el aviso más
 * serio de toda su superficie. El mismo candado fija que el aviso no se suavice
 * («para siempre» y «no se puede deshacer» / «permanently» y «cannot be undone»)
 * y que el botón siga diciendo lo que va a pasar en vez de «Eliminar» a secas.
 */

